/**
 * React Query hooks + key catalogue for the chat substrate (issue #351).
 *
 * Centralises the cache keys so ``useChatSSE`` can invalidate them
 * cleanly when SSE events arrive.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminDeleteMessage,
  adminEnsureThread,
  adminGetMessages,
  adminMarkRead,
  adminReply,
  ChatMessage,
  ChatThread,
  clearAssistantThread,
  getMessages,
  listAdminThreads,
  listAdminThreadsAll,
  listThreads,
  markRead,
  postMessage,
  AdminChatThread,
} from '../services/chatApi';

export const chatKeys = {
  all: ['chat'] as const,
  threads: () => [...chatKeys.all, 'threads'] as const,
  adminThreads: () => [...chatKeys.all, 'admin', 'threads'] as const,
  adminThreadsAll: () => [...chatKeys.all, 'admin', 'threads', 'all'] as const,
  threadMessages: (threadId: string) =>
    [...chatKeys.all, 'thread', threadId, 'messages'] as const,
  unread: () => [...chatKeys.all, 'unread'] as const,
};

export function useChatThreads() {
  return useQuery<ChatThread[]>({
    queryKey: chatKeys.threads(),
    queryFn: listThreads,
    staleTime: 60_000,
  });
}

export function useAdminChatThreads() {
  return useQuery<AdminChatThread[]>({
    queryKey: chatKeys.adminThreads(),
    queryFn: listAdminThreads,
    staleTime: 30_000,
  });
}

/**
 * Cross-tenant admin feed (every user's support thread, regardless of
 * which tenant owns it). Used by the admin left panel + the admin
 * home unread badge.
 */
export function useAdminChatThreadsAll() {
  return useQuery<AdminChatThread[]>({
    queryKey: chatKeys.adminThreadsAll(),
    queryFn: listAdminThreadsAll,
    staleTime: 30_000,
  });
}

export function useChatMessages(threadId: string | undefined) {
  return useQuery<ChatMessage[]>({
    queryKey: threadId ? chatKeys.threadMessages(threadId) : ['chat', 'thread', 'idle'],
    queryFn: () => getMessages(threadId as string),
    enabled: Boolean(threadId),
    staleTime: 10_000,
  });
}

/**
 * Admin-side message fetch — required when the admin reads a thread
 * that lives in a foreign tenant. Falls back to the user-side hook
 * when ``tenantId`` matches the calling admin's own tenant (the user
 * endpoint already has that data cached).
 */
export function useAdminChatMessages(
  threadId: string | undefined,
  tenantId: string | undefined,
) {
  return useQuery<ChatMessage[]>({
    queryKey: threadId
      ? [...chatKeys.threadMessages(threadId), 'admin', tenantId ?? '']
      : ['chat', 'thread', 'admin', 'idle'],
    queryFn: () => adminGetMessages(threadId as string, tenantId as string),
    enabled: Boolean(threadId && tenantId),
    staleTime: 10_000,
  });
}

export function usePostChatMessage(threadId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ body, sourceUrl }: { body: string; sourceUrl: string | null }) => {
      if (!threadId) throw new Error('thread_id required');
      return postMessage(threadId, body, sourceUrl);
    },
    onSuccess: () => {
      if (threadId) qc.invalidateQueries({ queryKey: chatKeys.threadMessages(threadId) });
      qc.invalidateQueries({ queryKey: chatKeys.threads() });
    },
  });
}

export function useMarkChatRead(threadId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => {
      if (!threadId) throw new Error('thread_id required');
      return markRead(threadId, messageId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chatKeys.unread() });
      // Refetch ``threads`` so ``thread.unread_count`` shrinks in
      // ``TechsupportTab`` (the server LEFT JOIN re-runs against
      // the freshly-written ``chat_message_reads`` rows).
      qc.invalidateQueries({ queryKey: chatKeys.threads() });
      if (threadId) qc.invalidateQueries({ queryKey: chatKeys.threadMessages(threadId) });
    },
  });
}

/**
 * Clear (non-destructively hide) the user's own conversation (#1732).
 *
 * On success, invalidate the thread's message list (now empty up to the
 * watermark), the threads list + unread key (so the #1735 FAB badge and
 * thread row reflect the post-clear unread state). The clear is a
 * user-facing hide: admin/admin-MCP reads keep the full history.
 */
export function useClearChatConversation(threadId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!threadId) throw new Error('thread_id required');
      return clearAssistantThread(threadId);
    },
    onSuccess: () => {
      if (threadId) qc.invalidateQueries({ queryKey: chatKeys.threadMessages(threadId) });
      qc.invalidateQueries({ queryKey: chatKeys.threads() });
      qc.invalidateQueries({ queryKey: chatKeys.unread() });
    },
  });
}

export function useAdminMarkRead(
  threadId: string | undefined,
  tenantId?: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => {
      if (!threadId) throw new Error('thread_id required');
      return adminMarkRead(threadId, messageId, tenantId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chatKeys.adminThreads() });
      qc.invalidateQueries({ queryKey: chatKeys.adminThreadsAll() });
    },
  });
}

export function useAdminReply(
  threadId: string | undefined,
  tenantId?: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => {
      if (!threadId) throw new Error('thread_id required');
      return adminReply(threadId, body, tenantId);
    },
    onSuccess: () => {
      if (threadId) qc.invalidateQueries({ queryKey: chatKeys.threadMessages(threadId) });
      qc.invalidateQueries({ queryKey: chatKeys.adminThreads() });
      qc.invalidateQueries({ queryKey: chatKeys.adminThreadsAll() });
    },
  });
}

export function useAdminDeleteMessage(
  threadId: string | undefined,
  tenantId?: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => adminDeleteMessage(messageId, tenantId),
    onSuccess: () => {
      if (threadId) qc.invalidateQueries({ queryKey: chatKeys.threadMessages(threadId) });
      qc.invalidateQueries({ queryKey: chatKeys.adminThreads() });
      qc.invalidateQueries({ queryKey: chatKeys.adminThreadsAll() });
    },
  });
}

export function useAdminEnsureThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, threadKind }: { userId: string; threadKind?: string }) =>
      adminEnsureThread(userId, threadKind),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chatKeys.adminThreads() });
      qc.invalidateQueries({ queryKey: chatKeys.adminThreadsAll() });
    },
  });
}
